import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { User, Copy, Check, Share2, LayoutDashboard, Link, Bell, ThumbsUp, ThumbsDown, MessageCircleQuestion, Bug, Eye, MoreVertical, ExternalLink, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import databullLogo from "@/assets/databulllogo.png";
import { useAuth } from "@/hooks/useAuth";
import { APP_NAME } from "@/lib/brand";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

/**
 * Returns true when the assistant message looks like a clarifying question —
 * i.e. the last non-empty line ends with a "?" and there are no large code
 * blocks (which would indicate an analytical response that happens to have
 * a trailing rhetorical question).
 */
function isClarifyingQuestion(content: string): boolean {
  if (!content?.trim()) return false;
  // Skip messages that are primarily code / SQL blocks
  const codeBlockCount = (content.match(/```/g) ?? []).length;
  if (codeBlockCount >= 2) return false;
  // Check if the last meaningful line ends with "?"
  const lines = content.trim().split("\n").filter((l) => l.trim().length > 0);
  const lastLine = lines[lines.length - 1]?.trim() ?? "";
  return lastLine.endsWith("?");
}

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
  usedTools?: boolean;
  createdAt?: string;
  isStreaming?: boolean;
  onGenerateDashboard?: (content: string, messageId?: string) => void;
  messageId?: string;
  hasDashboard?: boolean;
  dashboardTitle?: string;
  onViewDashboard?: (messageId: string) => void;
  dashboardSnapshotId?: string;
  onSubscribeDashboard?: (snapshotId: string, title: string) => void;
  /** Called when the user clicks the debug (Bug) icon on an assistant message */
  onDebug?: () => void;
  /** Called when user clicks "Open in Dashboard Builder" from dashboard kebab menu */
  onOpenInBuilder?: (snapshotId: string) => void;
  /** Called when user clicks "Download PDF" from dashboard kebab menu */
  onDownloadPdf?: (snapshotId: string, title: string) => void;
  /** Optional function to mask PII in displayed content */
  maskText?: (text: string) => string;
  /** Called when user wants to unmask PII in this message. Returns true if allowed. */
  onRequestUnmask?: (key: string, fieldName?: string) => Promise<boolean>;
}

const ChatMessage = ({ role, content, usedTools, createdAt, isStreaming, onGenerateDashboard, messageId, hasDashboard, dashboardTitle, onViewDashboard, dashboardSnapshotId, onSubscribeDashboard, onDebug, onOpenInBuilder, onDownloadPdf, maskText: maskTextFn, onRequestUnmask }: ChatMessageProps) => {
  const { clientInfo } = useAuth();
  const isAssistant = role === "assistant";
  const assistantLogo = clientInfo?.logo_url || databullLogo;
  const [messageUnmasked, setMessageUnmasked] = useState(false);
  const maskedContent = maskTextFn ? maskTextFn(content) : content;
  const hasMaskedContent = maskTextFn ? maskedContent !== content : false;
  const displayContent = messageUnmasked ? content : maskedContent;
  const isQuestion = isAssistant && !isStreaming && isClarifyingQuestion(displayContent);
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Load existing feedback for this message
  useEffect(() => {
    if (!isAssistant || !messageId) return;
    const loadFeedback = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("message_feedback" as any)
        .select("feedback")
        .eq("message_id", messageId)
        .eq("profile_id", user.id)
        .maybeSingle();
      if (data) setFeedback((data as any).feedback as "up" | "down");
    };
    loadFeedback();
  }, [messageId, isAssistant]);

  const handleFeedback = async (type: "up" | "down") => {
    if (!messageId || feedbackLoading) return;
    setFeedbackLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (feedback === type) {
        // Remove feedback
        await (supabase.from("message_feedback" as any) as any).delete()
          .eq("message_id", messageId)
          .eq("profile_id", user.id);
        setFeedback(null);
      } else {
        // Upsert feedback
        await (supabase.from("message_feedback" as any) as any).upsert(
          {
            message_id: messageId,
            profile_id: user.id,
            feedback: type,
            conversation_id: new URLSearchParams(window.location.search).get("conv") || "",
          },
          { onConflict: "message_id,profile_id" }
        );
        setFeedback(type);
      }
    } catch (e) {
      console.error("Feedback error:", e);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ text: displayContent });
    } else {
      await navigator.clipboard.writeText(displayContent);
    }
  };

  const handleCopyUrl = async () => {
    const url = new URL(window.location.href);
    url.search = "";
    if (messageId) {
      const convParam = new URLSearchParams(window.location.search).get("conv");
      if (convParam) url.searchParams.set("conv", convParam);
    }
    await navigator.clipboard.writeText(url.toString());
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  return (
    <div className={cn("group relative flex gap-4 px-4 py-6", isAssistant ? "bg-card/50" : "")}>
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          isAssistant ? "text-primary-foreground" : "bg-secondary text-secondary-foreground"
        )}
      >
        {isAssistant ? <img src={assistantLogo} alt={APP_NAME} className="h-7 w-7 rounded-lg object-cover" /> : <User className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2 flex-wrap">
          <p className="text-xs font-medium text-muted-foreground">
            {isAssistant ? `${APP_NAME} Assistant` : "You"}
          </p>
          {createdAt && (
            <span className="text-[10px] text-muted-foreground/60">
              {new Date(createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {isQuestion && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
              <MessageCircleQuestion className="h-3 w-3" />
              Needs your input
            </span>
          )}
        </div>
        <div className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground prose-a:text-primary">
          <ReactMarkdown>{displayContent}</ReactMarkdown>
        </div>
        {isAssistant && displayContent && (
          <div className="mt-3 flex items-center gap-2">
            {hasDashboard && onViewDashboard && messageId ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onViewDashboard(messageId)}
                  className="relative inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-primary bg-card cursor-pointer transition-transform duration-200 hover:scale-105 group/dash"
                >
                  <span className="absolute inset-0 rounded-lg border border-primary/50 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
                  <span className="absolute inset-0 rounded-lg bg-primary/5" />
                  <LayoutDashboard className="relative h-3.5 w-3.5" />
                  <span className="relative">{dashboardTitle || "Dashboard"}</span>
                </button>
                {dashboardSnapshotId && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Dashboard options">
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[180px]">
                      {onOpenInBuilder && (
                        <DropdownMenuItem onClick={() => onOpenInBuilder(dashboardSnapshotId)}>
                          <ExternalLink className="h-3.5 w-3.5 mr-2" />
                          Open in Dashboard Builder
                        </DropdownMenuItem>
                      )}
                      {onDownloadPdf && (
                        <DropdownMenuItem onClick={() => onDownloadPdf(dashboardSnapshotId, dashboardTitle || "Dashboard")}>
                          <FileDown className="h-3.5 w-3.5 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ) : (
              onGenerateDashboard && displayContent.length >= 500 && !displayContent.startsWith("Sorry") && !displayContent.toLowerCase().includes("error occurred") && !isStreaming && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  onClick={() => onGenerateDashboard(content, messageId)}
                >
                  <LayoutDashboard className="h-3.5 w-3.5" />
                  Generate Dashboard
                </Button>
              )
            )}
            <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleCopy}
                title="Copy to clipboard"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
            {messageId && (
              <div className="flex gap-1 ml-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-7 w-7", feedback === "up" && "text-primary")}
                  onClick={() => handleFeedback("up")}
                  disabled={feedbackLoading}
                  title="Good response"
                >
                  <ThumbsUp className={cn("h-3.5 w-3.5", feedback === "up" && "fill-current")} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-7 w-7", feedback === "down" && "text-destructive")}
                  onClick={() => handleFeedback("down")}
                  disabled={feedbackLoading}
                  title="Poor response"
                >
                  <ThumbsDown className={cn("h-3.5 w-3.5", feedback === "down" && "fill-current")} />
                </Button>
              </div>
            )}
            {hasMaskedContent && !messageUnmasked && onRequestUnmask && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                onClick={async () => {
                  const key = `chat-msg-${messageId || Date.now()}`;
                  const allowed = await onRequestUnmask(key, "chat_message");
                  if (allowed) setMessageUnmasked(true);
                }}
                title="Unmask PII in this message"
              >
                <Eye className="h-3.5 w-3.5" />
                Unmask
              </Button>
            )}
            <div className="opacity-0 transition-opacity group-hover:opacity-100">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="More options">
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[160px]">
                  {dashboardSnapshotId && onSubscribeDashboard && (
                    <DropdownMenuItem onClick={() => onSubscribeDashboard(dashboardSnapshotId, dashboardTitle || "Dashboard")}>
                      <Bell className="h-3.5 w-3.5 mr-2" />
                      Subscribe
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleShare}>
                    <Share2 className="h-3.5 w-3.5 mr-2" />
                    Share
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCopyUrl}>
                    {urlCopied ? <Check className="h-3.5 w-3.5 mr-2 text-primary" /> : <Link className="h-3.5 w-3.5 mr-2" />}
                    {urlCopied ? "Copied!" : "Copy URL"}
                  </DropdownMenuItem>
                  {onDebug && messageId && !isStreaming && (
                    <DropdownMenuItem onClick={onDebug}>
                      <Bug className="h-3.5 w-3.5 mr-2" />
                      Debug
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
